/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { DeviceModel } from "../device/DeviceModel";
import { Log } from "../logging/Log";
import { PlayerState } from "../videoplayer/enums";
import { VideoPlaybackController } from "../videoplayer/VideoPlaybackController";
import type {
  TActiveDrmInfo,
  TAudioTrack,
  TPlayerStats,
  TVariantTrack
} from "../videoplayer/types";
import {
  DebugBufferingSubState,
  DebugPlaybackType
} from "./videoDebugOverlay.types";
import type {
  IVideoMetricsProvider,
  TAbrVariant,
  TDebugMetrics
} from "./videoDebugOverlay.types";
import {
  createEmptyDebugMetrics,
  estimateBufferedBytes
} from "./videoDebugOverlay.utils";

/**
 * The key a viewer last pressed, rendered as `keyCode (name)`.
 *
 * Module state rather than a reading, because no API reports it: the
 * value only exists if something records it as keys go past. The view
 * layer's input hook calls {@link recordLastKeyCode} from the same
 * capture-phase listener it already installs, so this costs no extra
 * listener and sees every key — including ones the navigation engine
 * drops as unmapped, which are exactly the ones a key-map bug is about.
 */
let lastKeyCode: string | null = null;

/**
 * Record a key press for the overlay's "Last Key" row.
 *
 * `keyCode` leads because that is what a TV remote reports and what a
 * platform key-map is debugged against; `key` follows for anyone reading
 * the panel on a desktop keyboard.
 *
 * @param event - The raw keyboard event.
 */
export function recordLastKeyCode(event: KeyboardEvent): void {
  const name: string =
    typeof event.key === "string" && event.key.length > 0
      ? event.key
      : "unknown";
  lastKeyCode = `${event.keyCode} (${name})`;
}

const TAG: string = "WebVideoMetricsProvider";

/**
 * Buffer level at or below which a buffering player is considered starved
 * rather than filling ahead. Not zero: the element reports the buffer a
 * fraction of a second before it truly empties, so an exact-zero test
 * labels a genuine stall "Normal" for the tick that matters most.
 */
const STARVED_BUFFER_THRESHOLD_SECONDS: number = 0.5;

const MILLISECONDS_PER_SECOND: number = 1000;

/**
 * Widevine `videoRobustness` values that mean hardware-backed decryption,
 * i.e. security level L1. Everything else is L3. Mapped here because EME
 * exposes robustness but never the level, and "Widevine" alone does not
 * tell you whether a device is allowed to render HD.
 */
const HARDWARE_ROBUSTNESS: ReadonlySet<string> = new Set<string>([
  "HW_SECURE_ALL",
  "HW_SECURE_CRYPTO",
  "HW_SECURE_DECODE"
]);

/** EME key system id prefixes mapped to the names people actually use. */
const KEY_SYSTEM_NAMES: ReadonlyArray<[string, string]> = [
  ["com.widevine", "Widevine"],
  ["com.microsoft.playready", "PlayReady"],
  ["com.apple.fps", "FairPlay"],
  ["org.w3.clearkey", "ClearKey"]
];

/**
 * The slice of `MediaCapabilities` this module uses.
 *
 * Declared locally rather than relying on the DOM lib: the project's
 * TypeScript DOM typings predate the API on some toolchains, and a local
 * shape also forces the runtime guard below to stay honest about exactly
 * what is being called.
 */
interface TMediaCapabilities {
  decodingInfo(config: unknown): Promise<{
    powerEfficient: boolean;
    smooth: boolean;
    supported: boolean;
  }>;
}

/**
 * `MediaCapabilities.decodingInfo()`, or null where the platform has no
 * usable implementation.
 *
 * This single check is what gates the Hardware Accel row. It is a
 * capability probe rather than a browser-version comparison on purpose:
 * the API landed in Chrome 66 and Safari 13, but the question that
 * actually matters is whether *this* runtime implements it, and an
 * embedded TV browser's UA string is a poor guide to that. VegaOS,
 * Tizen and webOS are all Chromium derivatives whose engine version is
 * not reliably recoverable from the UA, so asking the object is both
 * simpler and more accurate than maintaining a version matrix.
 */
function getMediaCapabilities(): TMediaCapabilities | null {
  if (typeof navigator === "undefined") {
    return null;
  }
  const candidate: unknown = (
    navigator as unknown as { mediaCapabilities?: unknown }
  ).mediaCapabilities;
  if (candidate === null || typeof candidate !== "object") {
    return null;
  }
  const api = candidate as Partial<TMediaCapabilities>;
  return typeof api.decodingInfo === "function"
    ? (api as TMediaCapabilities)
    : null;
}

/**
 * Container assumed when asking about decode support.
 *
 * Every stream this app plays is fMP4/CMAF — DASH and HLS alike — so the
 * variant's codec string is enough and the track descriptor does not
 * need to carry a MIME type.
 */
const DECODE_PROBE_MIME: string = "video/mp4";

/** `performance.memory`, a non-standard Chromium extension. */
interface TPerformanceMemory {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
}

/**
 * Reads playback health out of the Web player stack for the debug overlay.
 *
 * Sources, in the order the proposal §3 lists the metrics:
 *   - **Shaka `getStats()`** — resolution, throughput, frame counters.
 *   - **Shaka variant tracks** — codecs, framerate, the ABR ladder, and
 *     the bitrate of the active rung.
 *   - **The media element**, via the player — buffered ranges.
 *   - **EME**, via the player — key system, robustness and key statuses.
 *   - **`DeviceModel` / `navigator` / `performance`** — environment.
 *
 * Two design rules hold throughout. First, **nothing throws**: each group
 * is read in its own `try`, so a Shaka API that changes shape between
 * versions costs one group of metrics rather than the whole panel, and a
 * failure can never propagate into playback. Second, **nothing is
 * invented**: a metric the platform does not report is `null`, never a
 * plausible-looking substitute — the one exception is the buffered byte
 * estimate, which is explicitly labelled as an estimate at render time
 * because MSE offers no byte-level measurement at all.
 *
 * The only state carried between snapshots is the bitrate dwell timer,
 * which cannot be derived from a single reading.
 */
export class WebVideoMetricsProvider implements IVideoMetricsProvider {
  /** Variant id the dwell timer is currently measuring, or null. */
  private mDwellVariantId: number | null = null;

  /** `Date.now()` at which the current variant became active. */
  private mDwellStartedAtMs: number = 0;

  /**
   * Identity of the rendition the hardware-acceleration probe was last
   * started for, so a probe runs once per rendition rather than once per
   * poll tick — `decodingInfo()` is asynchronous and not free.
   */
  private mDecodeProbeKey: string | null = null;

  /** Latest `powerEfficient` answer; null until a probe resolves. */
  private mIsHardwareAccelerated: boolean | null = null;

  public isPlaybackActive(): boolean {
    try {
      return VideoPlaybackController.getInstance().isPlayerLoaded();
    } catch (error: unknown) {
      Log.w(TAG, "isPlaybackActive()", error);
      return false;
    }
  }

  public reset(): void {
    this.mDwellVariantId = null;
    this.mDwellStartedAtMs = 0;
    this.mDecodeProbeKey = null;
    this.mIsHardwareAccelerated = null;
  }

  public getSnapshot(): TDebugMetrics {
    const metrics: TDebugMetrics = createEmptyDebugMetrics();

    const controller: VideoPlaybackController =
      VideoPlaybackController.getInstance();

    const stats: TPlayerStats | null = this.readStats(controller);
    const variants: ReadonlyArray<TVariantTrack> =
      this.readVariants(controller);
    const activeVariant: TVariantTrack | null = findActiveVariant(variants);

    this.fillIdentity(metrics, controller);
    this.fillVideo(metrics, stats, activeVariant, variants);
    this.fillAudio(metrics, controller, activeVariant);
    this.fillBandwidth(metrics, stats, activeVariant);
    this.fillBuffer(metrics, controller);
    this.fillFrames(metrics, stats);
    this.fillAbr(metrics, variants, activeVariant);
    this.probeHardwareAcceleration(activeVariant);
    this.fillDrm(metrics, controller);
    this.fillSource(metrics, controller);
    this.fillPlayerState(metrics, controller);
    fillDevice(metrics);

    return metrics;
  }

  // ── Readers ──────────────────────────────────────────────────────────────

  private readStats(controller: VideoPlaybackController): TPlayerStats | null {
    try {
      return controller.getPlayerStats();
    } catch (error: unknown) {
      Log.w(TAG, "readStats()", error);
      return null;
    }
  }

  private readVariants(
    controller: VideoPlaybackController
  ): ReadonlyArray<TVariantTrack> {
    try {
      const raw: unknown[] = controller.getVideoVariants();
      return raw as ReadonlyArray<TVariantTrack>;
    } catch (error: unknown) {
      Log.w(TAG, "readVariants()", error);
      return [];
    }
  }

  // ── Group fillers ────────────────────────────────────────────────────────

  private fillIdentity(
    metrics: TDebugMetrics,
    controller: VideoPlaybackController
  ): void {
    try {
      const playable = controller.getPlayable();
      const channel =
        playable !== null && playable.channel !== undefined
          ? playable.channel
          : null;

      metrics.identity.contentId = controller.getCurrentContentId();
      metrics.identity.playbackType = resolvePlaybackType(controller);

      if (channel !== null) {
        const channelNumber: string = controller.getCurrentChannelNumber();
        metrics.identity.channelNumber =
          channelNumber.length > 0 ? channelNumber : null;
        const callSign: unknown = channel.callSign;
        metrics.identity.channelCallSign =
          typeof callSign === "string" && callSign.length > 0 ? callSign : null;
      }
    } catch (error: unknown) {
      Log.w(TAG, "fillIdentity()", error);
    }
  }

  private fillVideo(
    metrics: TDebugMetrics,
    stats: TPlayerStats | null,
    activeVariant: TVariantTrack | null,
    variants: ReadonlyArray<TVariantTrack>
  ): void {
    try {
      if (stats !== null) {
        metrics.video.width = finiteOrNull(stats.width);
        metrics.video.height = finiteOrNull(stats.height);
      }

      if (activeVariant !== null) {
        metrics.video.frameRate = finiteOrNull(activeVariant.frameRate);
        metrics.video.codec =
          activeVariant.videoCodec !== null
            ? activeVariant.videoCodec
            : activeVariant.codecs;
      }

      const throughput: number | null =
        stats !== null ? finiteOrNull(stats.estimatedBandwidth) : null;
      const optimal: TVariantTrack | null = findOptimalVariant(
        variants,
        throughput
      );
      if (optimal !== null) {
        metrics.video.optimalWidth = finiteOrNull(optimal.width);
        metrics.video.optimalHeight = finiteOrNull(optimal.height);
      }
    } catch (error: unknown) {
      Log.w(TAG, "fillVideo()", error);
    }
  }

  private fillAudio(
    metrics: TDebugMetrics,
    controller: VideoPlaybackController,
    activeVariant: TVariantTrack | null
  ): void {
    try {
      const track: TAudioTrack | null = controller.getSelectedAudioTrack();
      if (track !== null) {
        metrics.audio.channelsCount = finiteOrNull(track.channelsCount);
        metrics.audio.codec = track.codec;
        metrics.audio.label = track.label;
        metrics.audio.language = track.language;
      } else if (activeVariant !== null) {
        metrics.audio.channelsCount = finiteOrNull(activeVariant.channelsCount);
        metrics.audio.codec = activeVariant.audioCodec;
        metrics.audio.language = activeVariant.language;
      }

      const volume: number = controller.getVolume();
      metrics.audio.volume = finiteOrNull(volume);
      // The controller exposes mute only as a command, so a zero level is
      // the sole muted signal available without widening its contract.
      metrics.audio.isMuted = volume === 0;
    } catch (error: unknown) {
      Log.w(TAG, "fillAudio()", error);
    }
  }

  private fillBandwidth(
    metrics: TDebugMetrics,
    stats: TPlayerStats | null,
    activeVariant: TVariantTrack | null
  ): void {
    try {
      let bitrate: number | null = null;
      if (activeVariant !== null) {
        bitrate = finiteOrNull(activeVariant.bandwidth);
      }
      if (bitrate === null && stats !== null) {
        bitrate = finiteOrNull(stats.streamBandwidth);
      }

      metrics.bandwidth.bitrateBps = bitrate;
      metrics.bandwidth.throughputBps =
        stats !== null ? finiteOrNull(stats.estimatedBandwidth) : null;
      metrics.bandwidth.secondsOnCurrentBitrate =
        this.trackBitrateDwell(activeVariant);
    } catch (error: unknown) {
      Log.w(TAG, "fillBandwidth()", error);
    }
  }

  private fillBuffer(
    metrics: TDebugMetrics,
    controller: VideoPlaybackController
  ): void {
    try {
      const aheadSeconds: number = controller.getBufferAheadSeconds();
      metrics.buffer.aheadSeconds = finiteOrNull(aheadSeconds);
      metrics.buffer.aheadBytesEstimate = estimateBufferedBytes(
        metrics.buffer.aheadSeconds,
        metrics.bandwidth.bitrateBps
      );
    } catch (error: unknown) {
      Log.w(TAG, "fillBuffer()", error);
    }
  }

  private fillFrames(metrics: TDebugMetrics, stats: TPlayerStats | null): void {
    if (stats === null) {
      return;
    }
    metrics.frames.corrupted = finiteOrNull(stats.corruptedFrames);
    metrics.frames.decoded = finiteOrNull(stats.decodedFrames);
    metrics.frames.dropped = finiteOrNull(stats.droppedFrames);
  }

  private fillAbr(
    metrics: TDebugMetrics,
    variants: ReadonlyArray<TVariantTrack>,
    activeVariant: TVariantTrack | null
  ): void {
    metrics.abr.selectedVariantId =
      activeVariant !== null ? activeVariant.id : null;
    metrics.abr.variants = variants.map(
      (variant: TVariantTrack): TAbrVariant => ({
        bandwidthBps: variant.bandwidth,
        frameRate: finiteOrNull(variant.frameRate),
        height: finiteOrNull(variant.height),
        id: variant.id,
        isSelected: variant.active,
        width: finiteOrNull(variant.width)
      })
    );
  }

  private fillDrm(
    metrics: TDebugMetrics,
    controller: VideoPlaybackController
  ): void {
    try {
      const active: TActiveDrmInfo | null = controller.getActiveDrmInfo();
      if (active !== null) {
        metrics.drm.keySystem = active.keySystem;
        metrics.drm.scheme = describeDrmScheme(
          active.keySystem,
          active.videoRobustness
        );
      } else {
        const configured: string | null = controller.getDrmServerType();
        metrics.drm.scheme = configured;
      }

      const statuses: Record<string, string> = controller.getKeyStatuses();
      const distinct: Array<string> = [];
      const keys: Array<string> = Object.keys(statuses);
      for (let i: number = 0; i < keys.length; i += 1) {
        const status: string = statuses[keys[i]];
        if (distinct.indexOf(status) === -1) {
          distinct.push(status);
        }
      }
      metrics.drm.keyStatuses = distinct;

      metrics.drm.isHardwareAccelerationDetectable =
        getMediaCapabilities() !== null;
      metrics.drm.isHardwareAccelerated = this.mIsHardwareAccelerated;
    } catch (error: unknown) {
      Log.w(TAG, "fillDrm()", error);
    }
  }

  /**
   * Ask the platform whether the active rendition decodes in a
   * power-efficient path, and cache the answer.
   *
   * Asynchronous by necessity — `decodingInfo()` returns a promise and
   * `getSnapshot()` cannot wait — so the poll reads whatever the last
   * probe settled on. The row shows `—` for the tick or two before the
   * first answer arrives, which is the honest rendering of "asked, not
   * answered yet".
   *
   * Re-probed only when the rendition changes, keyed on everything
   * `decodingInfo()` is given: ABR moving between rungs is exactly when
   * the answer can flip, and nothing else can change it.
   *
   * A late response for a rendition that is no longer active is
   * discarded — otherwise a slow probe could overwrite a newer answer.
   */
  private probeHardwareAcceleration(variant: TVariantTrack | null): void {
    const api: TMediaCapabilities | null = getMediaCapabilities();
    if (api === null) {
      return;
    }

    if (
      variant === null ||
      variant.videoCodec === null ||
      !Number.isFinite(variant.width) ||
      !Number.isFinite(variant.height)
    ) {
      this.mDecodeProbeKey = null;
      this.mIsHardwareAccelerated = null;
      return;
    }

    const key: string =
      `${variant.videoCodec}|${variant.width}x${variant.height}` +
      `@${variant.frameRate}|${variant.bandwidth}`;
    if (key === this.mDecodeProbeKey) {
      return;
    }
    this.mDecodeProbeKey = key;
    this.mIsHardwareAccelerated = null;

    // Called inside the executor — synchronously, as before — so an
    // implementation that throws rather than rejecting lands in the same
    // `.catch` instead of escaping into getSnapshot().
    new Promise<{ powerEfficient: boolean }>((resolve, reject): void => {
      api
        .decodingInfo({
          type: "media-source",
          video: {
            contentType: `${DECODE_PROBE_MIME}; codecs="${variant.videoCodec}"`,
            width: variant.width,
            height: variant.height,
            bitrate: variant.bandwidth,
            framerate: variant.frameRate !== null ? variant.frameRate : 30
          }
        })
        .then(resolve, reject);
    })
      .then((info: { powerEfficient: boolean }): void => {
        if (this.mDecodeProbeKey === key) {
          this.mIsHardwareAccelerated = info.powerEfficient;
        }
      })
      .catch((error: unknown): void => {
        if (this.mDecodeProbeKey === key) {
          this.mIsHardwareAccelerated = null;
        }
        Log.w(TAG, "probeHardwareAcceleration()", error);
      });
  }

  private fillSource(
    metrics: TDebugMetrics,
    controller: VideoPlaybackController
  ): void {
    try {
      const url: string | null = controller.getResolvedStreamingUrl();
      metrics.source.streamingUrl = url;
      metrics.source.cdnHost = extractHost(url);
    } catch (error: unknown) {
      Log.w(TAG, "fillSource()", error);
    }
  }

  private fillPlayerState(
    metrics: TDebugMetrics,
    controller: VideoPlaybackController
  ): void {
    try {
      const state: PlayerState | null = controller.getPlayerState();
      metrics.playerState.state = state !== null ? state : PlayerState.IDLE;

      if (metrics.playerState.state !== PlayerState.BUFFERING) {
        metrics.playerState.bufferingSubState = null;
        return;
      }

      const ahead: number | null = metrics.buffer.aheadSeconds;
      const isStarved: boolean =
        ahead === null || ahead <= STARVED_BUFFER_THRESHOLD_SECONDS;
      metrics.playerState.bufferingSubState = isStarved
        ? DebugBufferingSubState.STARVED
        : DebugBufferingSubState.NORMAL;
    } catch (error: unknown) {
      Log.w(TAG, "fillPlayerState()", error);
    }
  }

  /**
   * Seconds the player has held the active variant.
   *
   * Measured rather than read: no player API reports it, so the first
   * snapshot that sees a new variant id starts a clock. A fresh clock
   * reads 0 — correct, and distinguishable from `null`, which means the
   * player has no active variant at all.
   */
  private trackBitrateDwell(
    activeVariant: TVariantTrack | null
  ): number | null {
    if (activeVariant === null) {
      this.mDwellVariantId = null;
      return null;
    }

    const now: number = Date.now();

    if (this.mDwellVariantId !== activeVariant.id) {
      this.mDwellVariantId = activeVariant.id;
      this.mDwellStartedAtMs = now;
      return 0;
    }

    return (now - this.mDwellStartedAtMs) / MILLISECONDS_PER_SECOND;
  }
}

// ── Free helpers ───────────────────────────────────────────────────────────

/** Narrows a possibly-NaN player number to a usable value or null. */
function finiteOrNull(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function findActiveVariant(
  variants: ReadonlyArray<TVariantTrack>
): TVariantTrack | null {
  for (let i: number = 0; i < variants.length; i += 1) {
    if (variants[i].active) {
      return variants[i];
    }
  }
  return null;
}

/**
 * The highest-bandwidth rung the current throughput estimate could sustain.
 *
 * Compared against raw throughput with no safety margin on purpose: the
 * point of the field is to show the ceiling the network implies, so that a
 * gap against the active rung is attributable to ABR configuration or
 * device capability rather than to a margin this function chose.
 */
function findOptimalVariant(
  variants: ReadonlyArray<TVariantTrack>,
  throughputBps: number | null
): TVariantTrack | null {
  if (variants.length === 0 || throughputBps === null) {
    return null;
  }

  let best: TVariantTrack | null = null;
  for (let i: number = 0; i < variants.length; i += 1) {
    const variant: TVariantTrack = variants[i];
    if (variant.bandwidth > throughputBps) {
      continue;
    }
    if (best === null || variant.bandwidth > best.bandwidth) {
      best = variant;
    }
  }
  return best;
}

/**
 * Turn an EME key system id and its negotiated robustness into the label
 * people use — "Widevine L1" rather than "com.widevine.alpha".
 */
function describeDrmScheme(keySystem: string, videoRobustness: string): string {
  let name: string = keySystem;
  for (let i: number = 0; i < KEY_SYSTEM_NAMES.length; i += 1) {
    const [prefix, label] = KEY_SYSTEM_NAMES[i];
    if (keySystem.indexOf(prefix) === 0) {
      name = label;
      break;
    }
  }

  // Security level is a Widevine concept; PlayReady and FairPlay express
  // hardware backing differently and are not given a spurious L-number.
  if (name !== "Widevine" || videoRobustness.length === 0) {
    return name;
  }

  return HARDWARE_ROBUSTNESS.has(videoRobustness) ? `${name} L1` : `${name} L3`;
}

/**
 * Hostname of the edge serving the stream.
 *
 * Parsed from the resolved manifest URI because no browser API exposes the
 * CDN node directly, and after redirects the resolved host *is* the edge.
 * Hand-rolled rather than using `URL`, which is absent on the Chromium 52
 * Smart TV baseline this app still supports.
 */
function extractHost(url: string | null): string | null {
  if (url === null || url.length === 0) {
    return null;
  }
  const withoutScheme: string = url.replace(/^[a-zA-Z][\w+.-]*:\/\//, "");
  const end: number = firstIndexOfAny(withoutScheme, ["/", "?", "#"]);
  const authority: string =
    end === -1 ? withoutScheme : withoutScheme.substring(0, end);
  const atIndex: number = authority.lastIndexOf("@");
  const hostAndPort: string =
    atIndex === -1 ? authority : authority.substring(atIndex + 1);
  return hostAndPort.length > 0 ? hostAndPort : null;
}

function firstIndexOfAny(
  value: string,
  needles: ReadonlyArray<string>
): number {
  let found: number = -1;
  for (let i: number = 0; i < needles.length; i += 1) {
    const index: number = value.indexOf(needles[i]);
    if (index !== -1 && (found === -1 || index < found)) {
      found = index;
    }
  }
  return found;
}

function resolvePlaybackType(
  controller: VideoPlaybackController
): DebugPlaybackType {
  // Ordered most specific first: a Start Over stream is also "live" to the
  // controller, and a catch-up asset is also nDVR-backed.
  if (controller.isStartOver()) {
    return DebugPlaybackType.START_OVER;
  }
  if (controller.isCatchUp()) {
    return DebugPlaybackType.CATCH_UP;
  }
  if (controller.isNdvr()) {
    return DebugPlaybackType.NDVR;
  }
  if (controller.isVod()) {
    return DebugPlaybackType.VOD;
  }
  if (controller.isLive()) {
    return DebugPlaybackType.LIVE;
  }
  return DebugPlaybackType.UNKNOWN;
}

function readPerformanceMemory(): TPerformanceMemory | null {
  if (typeof performance === "undefined") {
    return null;
  }
  const candidate: unknown = (
    performance as unknown as {
      memory?: unknown;
    }
  ).memory;
  if (candidate === null || typeof candidate !== "object") {
    return null;
  }
  const memory = candidate as Partial<TPerformanceMemory>;
  if (typeof memory.usedJSHeapSize !== "number") {
    return null;
  }
  return {
    jsHeapSizeLimit:
      typeof memory.jsHeapSizeLimit === "number" ? memory.jsHeapSizeLimit : 0,
    totalJSHeapSize:
      typeof memory.totalJSHeapSize === "number"
        ? memory.totalJSHeapSize
        : memory.usedJSHeapSize,
    usedJSHeapSize: memory.usedJSHeapSize
  };
}

function fillDevice(metrics: TDebugMetrics): void {
  try {
    metrics.device.appVersion = DeviceModel.getAppVersionName();
    metrics.device.deviceId = DeviceModel.getDeviceUuid();
    metrics.device.lastKeyCode = lastKeyCode;
    metrics.device.localTime = new Date().toLocaleString();
    metrics.device.platform = describePlatform();

    const memory: TPerformanceMemory | null = readPerformanceMemory();
    if (memory !== null) {
      metrics.device.memoryTotalBytes = memory.totalJSHeapSize;
      metrics.device.memoryUsedBytes = memory.usedJSHeapSize;
    }
  } catch (error: unknown) {
    Log.w(TAG, "fillDevice()", error);
  }
}

function describePlatform(): string {
  const platformName: string = DeviceModel.getTVProperties().getPlatformName();
  const browser: string = `${DeviceModel.getBrowserName()} ${DeviceModel.getBrowserVersion()}`;
  const trimmedPlatform: string =
    typeof platformName === "string" ? platformName.trim() : "";
  return trimmedPlatform.length > 0
    ? `${trimmedPlatform} / ${browser.trim()}`
    : browser.trim();
}
